// One SSE connection per browser tab (ruled). Frames are pointers: the handler
// fetches the body by id through the normal API. The wrapper owns reconnect,
// the polling fallback and the duplicate-seq guard (the client half of the
// ordering rule). EMR-1874.
import { API_BASE_URL, client } from '../api';
import type {
  PollEventsEventsGetResponses,
  PollResponse,
} from '../api/types.gen';

// The generator inlines the discriminated union instead of exporting it under
// the backend's `UserEventFrame` name, so derive it from the one place the
// union does reach the client. Deriving beats hand-writing it: a new frame
// member on the backend lands here the next time the client is regenerated.
export type UserEventFrame = PollResponse['frames'][number];

export type EventStreamState = 'streaming' | 'backoff' | 'polling' | 'stopped';

const BACKOFF_MIN_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;
const BACKOFF_JITTER = 0.2;
const FAILURES_BEFORE_POLLING = 3;
// Mirrors the server's `event_stream_heartbeat_s` default. The server pings at
// least every ~25 s (20 s plus one 5 s wait), so 2.5 intervals of silence mean
// the path is dead even when no error ever fires (EMR-2004).
const HEARTBEAT_MS = 20_000;
const STALL_MS = HEARTBEAT_MS * 2.5;
const POLL_INTERVAL_MS = 30_000;
const STREAM_RETRY_MS = 5 * 60_000;
const HIDDEN_CLOSE_MS = 5 * 60_000;
// How long a dispatched seq is remembered. A tab left open for days must not
// grow `seen` for ever. This stays far above the server's 60 s
// RECENT_SEQ_MEMORY_S because the poll fallback re-issues the recovery read
// every 30 s and the client measures from arrival while the server measures
// from `created_at`.
const SEEN_TTL_MS = 10 * 60_000;

// The server writes `event: <frame type>` on every frame (spec: Stream
// handler), so EventSource routes it to the listener of that name and never to
// `onmessage`. A new event type adds its `UserEventType` value here.
const FRAME_EVENT_NAMES = ['chat.message', 'chat.attachment', 'reset'] as const;

type Handler = (frame: UserEventFrame) => void;

let source: EventSource | null = null;
let state: EventStreamState = 'stopped';
let cursor: number | null = null;
// Seqs already handed to a subscriber, against the time each was dispatched.
// Cleared on reset and on stop, and pruned on every dispatch.
let seen = new Map<number, number>();
let failures = 0;
let handlers = new Map<string, Set<Handler>>();
let resetHandlers = new Set<() => void>();
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let stallTimer: ReturnType<typeof setTimeout> | null = null;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let streamRetryTimer: ReturnType<typeof setInterval> | null = null;
let hiddenTimer: ReturnType<typeof setTimeout> | null = null;
let visibilityBound = false;
// One run is one `start()`..`stop()` life. Everything asynchronous carries the
// run it was scheduled in, so a poll or a reconnect that settles after a stop
// or a restart cannot touch the state of the run that replaced it.
let runId = 0;
// True between `start()` and `stop()`. `state` cannot serve here: it reads
// 'stopped' during the first `connect()` of a run.
let running = false;
// True while a hidden tab is closed down: no stream, no poll, no retry, until
// the tab comes back.
let suspended = false;
// True once a frame or a reset has decided where this client stands, so the
// opening poll of `start()` can never overwrite that answer.
let cursorDecided = false;

const clearTimer = (timer: ReturnType<typeof setTimeout> | null) => {
  if (timer !== null) {
    clearTimeout(timer);
  }
};

const streamUrl = (): string =>
  cursor === null
    ? `${API_BASE_URL}/events/stream`
    : `${API_BASE_URL}/events/stream?after=${cursor}`;

const dispatch = (frame: UserEventFrame): void => {
  cursorDecided = true;
  if (frame.type === 'reset') {
    // The server does not know this cursor. Forget it, so the next stream
    // connection opens with no `after` and asks for no backlog, and let the app
    // reload. On the poll path `pollOnce` re-stores the cursor the route
    // returned (its max seq) straight after this, which is the same restart.
    cursor = null;
    seen.clear();
    resetHandlers.forEach(handler => handler());
    return;
  }
  // The client half of the ordering rule: drop only a seq already handled. A
  // *lower* seq is not a duplicate — it is a transaction that took its seq
  // first and committed late, and the server delivers it under the 60 s rule.
  const now = Date.now();
  seen.forEach((at, seq) => {
    if (now - at > SEEN_TTL_MS) {
      seen.delete(seq);
    }
  });
  if (seen.has(frame.seq)) {
    return;
  }
  seen.set(frame.seq, now);
  // The cursor never moves backwards, so a late low seq is not replayed.
  cursor = cursor === null ? frame.seq : Math.max(cursor, frame.seq);
  handlers.get(frame.type)?.forEach(handler => handler(frame));
};

const closeSource = (): void => {
  clearTimer(stallTimer);
  stallTimer = null;
  source?.close();
  source = null;
};

const stopPolling = (): void => {
  if (pollTimer !== null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  if (streamRetryTimer !== null) {
    clearInterval(streamRetryTimer);
    streamRetryTimer = null;
  }
};

const pollOnce = async (run: number): Promise<void> => {
  try {
    // The first type argument is the generated status-code map, not the body:
    // the client resolves `data` to the map's member, the way `sdk.gen.ts` does.
    const response = await client.request<
      PollEventsEventsGetResponses,
      unknown,
      true
    >({
      method: 'GET',
      url: '/events',
      query: cursor === null ? undefined : { after: cursor },
      throwOnError: true,
    });
    // A response that arrives after its run ended belongs to nobody: it must
    // not dispatch, must not move a restarted run's cursor.
    if (run !== runId) {
      return;
    }
    const body = response.data;
    body.frames.forEach(dispatch);
    // Never move the cursor backwards: dispatch already advanced it per frame.
    cursor = cursor === null ? body.cursor : Math.max(cursor, body.cursor);
  } catch (error) {
    // A 401 on the fallback means the session is gone: stop instead of looping.
    // The api client's own interceptor has already cleared local auth state.
    // A stale 401 must not stop the run that replaced this one.
    if (
      run === runId &&
      (error as { response?: { status?: number } })?.response?.status === 401
    ) {
      stop();
    }
  }
};

// The stream connection waits on this read, so it must never hang: a stalled
// prime costs about five seconds of realtime, never the session.
const PRIME_TIMEOUT_MS = 5_000;

const primeCursor = async (run: number): Promise<void> => {
  // `GET /events` with no `after` answers the user's current max seq and no
  // frames. The connection then opens with that seq as its `after`, so a row
  // committed between this read and the connection is delivered, not skipped.
  try {
    const response = await client.request<
      PollEventsEventsGetResponses,
      unknown,
      true
    >({
      method: 'GET',
      url: '/events',
      timeout: PRIME_TIMEOUT_MS,
      throwOnError: true,
    });
    if (run !== runId || cursorDecided) {
      return;
    }
    // The body is typed, not proven: `api.ts` notes that the single-page
    // application fallback answers HTML with HTTP 200, so `cursor` can arrive
    // as anything. The opening connection now carries this value, so a
    // non-number must behave exactly like a failed prime — leave the cursor
    // unset and open with no `after`. Assigning it would open the stream at
    // `?after=undefined` (422) and leave the poll fallback computing
    // `Math.max(undefined, n)` = NaN, killing both paths for the whole run.
    const primed: unknown = response.data.cursor;
    if (typeof primed !== 'number' || !Number.isFinite(primed)) {
      return;
    }
    cursor = primed;
  } catch (error) {
    if (
      run === runId &&
      (error as { response?: { status?: number } })?.response?.status === 401
    ) {
      stop();
    }
  }
};

const startPolling = (): void => {
  // Set the state before the already-polling guard: a stream retry that fails
  // while polling comes back through `scheduleReconnect`, which leaves the
  // state at 'backoff' until this line puts it back.
  state = 'polling';
  if (pollTimer !== null) {
    return;
  }
  const run = runId;
  pollTimer = setInterval(() => void pollOnce(run), POLL_INTERVAL_MS);
  streamRetryTimer = setInterval(() => connect(), STREAM_RETRY_MS);
};

const scheduleReconnect = (): void => {
  state = 'backoff';
  // Never leave a second timer behind: an overwritten reference is a retry
  // `stop()` cannot cancel.
  clearTimer(reconnectTimer);
  const run = runId;
  const base = Math.min(BACKOFF_MIN_MS * 2 ** failures, BACKOFF_MAX_MS);
  const jitter = base * BACKOFF_JITTER * (Math.random() * 2 - 1);
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    void pollOnce(run).then(() => {
      if (run !== runId || !running || suspended) {
        return;
      }
      if (failures >= FAILURES_BEFORE_POLLING) {
        startPolling();
        return;
      }
      connect();
    });
  }, base + jitter);
};

const connect = (): void => {
  if (!running || suspended) {
    return;
  }
  // `connect()` is reached from a promise callback now (the cursor prime), so
  // a throw here is an unhandled rejection rather than an error React can
  // catch, and it fails the whole test run against whichever file happens to
  // be running. An environment with no `EventSource` — jsdom, a browser too
  // old for it — therefore takes the documented no-stream path instead of
  // throwing. Nothing is swallowed: the run polls and `state` says 'polling',
  // never 'streaming'.
  if (typeof EventSource === 'undefined') {
    startPolling();
    return;
  }
  clearTimer(reconnectTimer);
  reconnectTimer = null;
  closeSource();
  const next = new EventSource(streamUrl(), { withCredentials: true });
  const fail = () => {
    failures += 1;
    closeSource();
    scheduleReconnect();
  };
  // Proof of life is a frame or a second ping, never `onopen`: a path can open
  // and then deliver nothing, and that must still count towards polling. The
  // server pings at open before it reads anything, so a connection's first
  // ping re-arms the stall timer but does not clear `failures`: a service that
  // pings and then errors or stalls still reaches polling.
  let proved = false;
  const alive = () => {
    clearTimer(stallTimer);
    stallTimer = setTimeout(fail, STALL_MS);
  };
  const onPing = () => {
    if (proved) {
      failures = 0;
    }
    proved = true;
    alive();
  };
  next.onopen = () => {
    state = 'streaming';
    stopPolling();
  };
  const onFrame = (event: MessageEvent) => {
    failures = 0;
    proved = true;
    alive();
    dispatch(JSON.parse(event.data as string) as UserEventFrame);
  };
  FRAME_EVENT_NAMES.forEach(name =>
    next.addEventListener(name, onFrame as EventListener)
  );
  next.addEventListener('ping', onPing);
  next.onerror = fail;
  // Armed at creation, so an attempt that never sends a byte also fails.
  stallTimer = setTimeout(fail, STALL_MS);
  source = next;
  state = 'streaming';
};

const onVisibilityChange = (): void => {
  if (!running) {
    return;
  }
  if (document.visibilityState === 'hidden') {
    hiddenTimer = setTimeout(() => {
      // A hidden tab costs the server a connection and the fallback costs it a
      // request every 30 s. Suspend every one of them, not only the stream, or
      // the poll timer and the stream retry reopen what this closed.
      suspended = true;
      clearTimer(reconnectTimer);
      reconnectTimer = null;
      stopPolling();
      closeSource();
      state = 'backoff';
    }, HIDDEN_CLOSE_MS);
    return;
  }
  clearTimer(hiddenTimer);
  hiddenTimer = null;
  suspended = false;
  if (source === null) {
    // Cancels any reconnect still pending, so a stale backoff callback cannot
    // replace the connection opened here.
    failures = 0;
    connect();
  }
};

const start = (): void => {
  if (running) {
    return;
  }
  if (!visibilityBound) {
    document.addEventListener('visibilitychange', onVisibilityChange);
    visibilityBound = true;
  }
  running = true;
  suspended = false;
  failures = 0;
  runId += 1;
  const run = runId;
  if (cursor !== null) {
    connect();
    return;
  }
  // Nothing is known yet. Read the cursor first and open the connection at it:
  // the server resumes from that seq, so a row committed while the connection
  // was being made is delivered instead of being claimed by a stored max seq.
  // A prime that fails or times out opens with no cursor, which is the older
  // behaviour: realtime is late by `PRIME_TIMEOUT_MS`, never lost for the run.
  cursorDecided = false;
  void primeCursor(run).then(() => {
    if (run !== runId) {
      return;
    }
    connect();
  });
};

const stop = (): void => {
  running = false;
  suspended = false;
  runId += 1; // anything already in flight belongs to the run that just ended
  clearTimer(reconnectTimer);
  reconnectTimer = null;
  clearTimer(hiddenTimer);
  hiddenTimer = null;
  stopPolling();
  closeSource();
  // The next start can belong to another user on the same tab (sign-out, then
  // sign-in), so nothing of this run's position may carry over.
  cursor = null;
  cursorDecided = false;
  seen = new Map();
  state = 'stopped';
};

export const events = {
  start,
  stop,
  subscribe<T extends UserEventFrame['type']>(
    type: T,
    handler: (frame: Extract<UserEventFrame, { type: T }>) => void
  ): () => void {
    const set = handlers.get(type) ?? new Set<Handler>();
    set.add(handler as Handler);
    handlers.set(type, set);
    return () => set.delete(handler as Handler);
  },
  onReset(handler: () => void): () => void {
    resetHandlers.add(handler);
    return () => resetHandlers.delete(handler);
  },
  get state(): EventStreamState {
    return state;
  },
};

export function resetEventsForTests(): void {
  stop();
  cursor = null;
  cursorDecided = false;
  seen = new Map();
  failures = 0;
  handlers = new Map();
  resetHandlers = new Set();
  if (visibilityBound) {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    visibilityBound = false;
  }
}
